import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {getHTMLElementHeight} from '../../helpers/index'
import classNames from "classnames";
import PropTypes from 'prop-types';
import "./ScrollableArea.scss";
import useDeviceSizeDetect from "../../hooks/useDeviceSizeDetect";

const SCROLL_BOX_MIN_HEIGHT = 20;

const isScrollable = ref => {
  const el = ref && ref.current;
  if(!el) return false;
  const childrenHeight = Array.from(el.children)
    .reduce((acc, child) => {
      acc += getHTMLElementHeight(child);
      return acc;
    }, 0);
  const refHeight = getHTMLElementHeight(el);
  
  return childrenHeight > refHeight;
}

export default function ScrollableArea({ children, className, ...restProps }) {
  const detectMobile = useDeviceSizeDetect();
  const [hovering, setHovering] = useState(detectMobile.isMobile);
  const [scrollBoxHeight, setScrollBoxHeight] = useState(SCROLL_BOX_MIN_HEIGHT);
  const [scrollBoxTop, setScrollBoxTop] = useState(0);
  const [lastScrollThumbPosition, setScrollThumbPosition] = useState(0);
  const [isDragging, setDragging] = useState(false);
  const scrollHostRef = useRef();
  const isScrollVisible = useMemo(() => {
    return (scrollHostRef?.current?.offsetHeight || 0) > scrollBoxHeight
  }, [scrollHostRef.current, scrollBoxHeight]);

  const handleMouseOver = useCallback(() => {
    !hovering && isScrollable(scrollHostRef) && setHovering(true);
  }, [hovering]);
  
  const handleMouseOut = useCallback(() => {
    !!hovering && isScrollable(scrollHostRef) && setHovering(false);
  }, [hovering]);
  
  const handleDocumentMouseUp = useCallback(
    e => {
      if (isDragging) {
        e.preventDefault();
        setDragging(false);
      }
    },
    [isDragging]
  );
  
  
  const handleDocumentMouseMove = useCallback(
    e => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
        const scrollHostElement = scrollHostRef.current;
        const { scrollHeight, offsetHeight } = scrollHostElement;
        
        let deltaY = e.clientY - lastScrollThumbPosition;
        let percentage = deltaY * (scrollHeight / offsetHeight);
        
        setScrollThumbPosition(e.clientY);
        setScrollBoxTop(
          Math.min(
            Math.max(0, scrollBoxTop + deltaY),
            offsetHeight - scrollBoxHeight
          )
        );
        scrollHostElement.scrollTop = Math.min(
          scrollHostElement.scrollTop + percentage,
          scrollHeight - offsetHeight
        );
      }
    },
    [isDragging, lastScrollThumbPosition, scrollBoxHeight, scrollBoxTop]
  );
  
  const handleScrollThumbMouseDown = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setScrollThumbPosition(e.clientY);
    setDragging(true);
  }, []);
  
  const handleScroll = useCallback(() => {
    if (!scrollHostRef) {
      return;
    }
    const scrollHostElement = scrollHostRef.current;
    const { scrollTop, scrollHeight, offsetHeight } = scrollHostElement;
  
    let newTop =
      (parseInt(scrollTop, 10) / parseInt(scrollHeight, 10)) * offsetHeight;
    // newTop = newTop + parseInt(scrollTop, 10);
    newTop = Math.min(newTop, offsetHeight - scrollBoxHeight);
    setScrollBoxTop(newTop);
  }, []);
  
  useEffect(() => {
    const scrollHostElement = scrollHostRef.current;
    const { clientHeight, scrollHeight } = scrollHostElement;
    const scrollThumbPercentage = clientHeight / scrollHeight;
    const scrollThumbHeight = Math.max(
      scrollThumbPercentage * clientHeight,
      SCROLL_BOX_MIN_HEIGHT
    );
    setScrollBoxHeight(scrollThumbHeight);
  }, [children])

  useEffect(() => {
    //this is handle the dragging on scroll-thumb
    document.addEventListener("mousemove", handleDocumentMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);
    document.addEventListener("mouseleave", handleDocumentMouseUp);
    return function cleanup() {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
      document.removeEventListener("mouseleave", handleDocumentMouseUp);
    };
  }, [handleDocumentMouseMove, handleDocumentMouseUp]);

  useEffect(() => {
    const scrollHostElement = scrollHostRef.current;
    scrollHostElement.parentNode.parentNode.style.boxSizing = 'content-box';
    scrollHostElement.addEventListener("scroll", handleScroll, true);
    return function cleanup() {
      scrollHostElement.removeEventListener("scroll", handleScroll, true);
    };
  }, []);
  return (
    <div
      className={classNames(['scrollhost-container', className])}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <div
        ref={scrollHostRef}
        className="scrollhost"
        {...restProps}
      >
        {children}
      </div>
      <div
        className={"scroll-bar"}
        style={{ opacity: hovering && isScrollVisible ? 1 : 0 }}
      >
        <div
          className={"scroll-thumb"}
          style={{ height: scrollBoxHeight || 0, top: scrollBoxTop || 0 }}
          onMouseDown={handleScrollThumbMouseDown}
        />
      </div>
    </div>
  );
}

ScrollableArea.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  className: PropTypes.string,
}